/* eslint-disable max-lines */
import { createSelector } from '@reduxjs/toolkit';
import {
    abProfitableTariffsFirstTouch,
    additionalTariffsList,
    desktopTariffMinSize,
    touchTariffMinSize,
} from 'reactApp/appHelpers/featuresHelpers';
import { cheapestTariff } from 'reactApp/appHelpers/featuresHelpers/features/cheapestTariff';
import { excludeTariffs } from 'reactApp/appHelpers/featuresHelpers/features/excludeTariffsByQuota';
import { excludeTariffsForFreeUsers } from 'reactApp/appHelpers/featuresHelpers/features/excludeTariffsForFreeUsers';
import { excludeTariffsMobileOverquoters } from 'reactApp/appHelpers/featuresHelpers/features/excludeTariffsForOverquoters';
import { enabledFamilyTariff, familyTariffIdFromQuery, familyTariffIds } from 'reactApp/appHelpers/featuresHelpers/features/familyTariff';
import { favorableTariffs as favorableTariffsIDs } from 'reactApp/appHelpers/featuresHelpers/features/favorableTariffs';
import { enabledExpensivePlanTrial, expensivePlanTariffIds } from 'reactApp/appHelpers/featuresHelpers/features/forceTrial';
import {
    isPromoQuotaWithLitress,
    litresTariffIds3mDiscountList,
    litresTariffIdsList,
} from 'reactApp/appHelpers/featuresHelpers/features/litres';
import { mayPromo2025YearProduct, mayPromo2025YearProductId } from 'reactApp/appHelpers/featuresHelpers/features/mayPromo2025';
import {
    multiTariffAdvancedTariffList,
    multiTariffBasicTariffList,
    multiTariffFamilyTariff,
} from 'reactApp/appHelpers/featuresHelpers/features/multiTariff';
import {
    multiTariffAdvancedTariffListMobile,
    multiTariffBasicTariffListMobile,
    multiTariffFamilyTariffListMobile,
} from 'reactApp/appHelpers/featuresHelpers/features/multiTariffMobile';
import {
    isMultiTariffAdvancedOverquota,
    multiTariffAdvancedOverquotaList,
} from 'reactApp/appHelpers/featuresHelpers/features/multiTariffOverquota';
import { isPaidUserPrices } from 'reactApp/appHelpers/featuresHelpers/features/paidUserPrices';
import { referralProgramTariffs } from 'reactApp/appHelpers/featuresHelpers/features/referralProgram';
import { tariffOverquotaList } from 'reactApp/appHelpers/featuresHelpers/features/tariffOverquotaList';
import { SPACE_LIST } from 'reactApp/constants/spaceList';
import { getBaseUserQuota } from 'reactApp/modules/family/family.selectors';
import { cheapestTariffID } from 'reactApp/modules/products/products.const';
import { getProductFromTariffListByQuotaAndPeriod, getTariffsByQuotaHelper, SPACE_SIZES } from 'reactApp/modules/products/products.helpers';
import { ProductsSelectors } from 'reactApp/modules/products/products.selectors';
import type { YearAndMonthProductIds } from 'reactApp/modules/products/products.types';
import { getMultiTariffMobileActiveTab } from 'reactApp/modules/quotaLanding/quotaLanding.selector';
import { ReferralProgramSelectors } from 'reactApp/modules/referralProgram/referralProgram.selectors';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { normalizeMobileTariffList } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.helpers';
import type { ITariffsMobile } from 'reactApp/sections/MobileSubscriptionsPage/MobileSubscriptionsPage.types';
import type { RootState } from 'reactApp/store';
import type { Product, Tariff } from 'reactApp/types/Billing';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import type { ITariffsByTab } from 'reactApp/ui/TariffSectionNew/TariffsSection.types';
import { getDiscount } from 'reactApp/utils/getDiscount';
import { getMonthes, getTariffProductsByPeriods, PERIODS } from 'reactApp/utils/Period';

import { baseYearProduct } from './TariffsPlans.helpers';

const RECOMMENDED_ID = 'W256G';
const RECOMMENDED_ID_FAVORABLE = 'W1T';

export const getMaxTariffDiscount = createSelector(
    [
        (state: RootState) => ProductsSelectors.getTariffsForQuotaLanding(state),
        (state) => ProductsSelectors.getPromoTariffs(state, true),
        (state, isMobile) => isMobile,
        // tempexp_16373-next-line
        (state) => ProductsSelectors.getFavorableTariffList(state),
        (state) => ProductsSelectors.getPreferredTariffs(state),
        // tempexp_18086-start
        (state: RootState) => ReferralProgramSelectors.getRefId(state),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(referralProgramTariffs?.month || []),
                ...(referralProgramTariffs?.year || []),
            ]),
        // tempexp_18086-end
    ],
    // tempexp_16373-next-line
    (
        nonSpecialTariffs,
        promoTariffs,
        isMobile,
        favorableTariffs,
        preferredTariffs,
        // tempexp_18086-start
        ref_id,
        referralTariffs
        // tempexp_18086-end
    ) => {
        let tariffs: Tariff[] = promoTariffs?.length ? promoTariffs : nonSpecialTariffs;

        const minTariffSize = SPACE_SIZES[`_${isMobile ? touchTariffMinSize : desktopTariffMinSize}`];
        tariffs = tariffs.filter((tariff) => tariff?.space?.original >= minTariffSize);

        if (favorableTariffs.length) {
            tariffs = favorableTariffs;
        }

        if (preferredTariffs.length) {
            tariffs = preferredTariffs;
        }

        // tempexp_17380-next-line
        if (isPaidUserPrices) {
            tariffs = nonSpecialTariffs;
        }

        let discounts = tariffs.map((tariffItem) => {
            const { monthProduct, yearProduct } = getTariffProductsByPeriods(tariffItem.products);

            if (!(monthProduct && yearProduct)) {
                return 0;
            }

            const yearPriceByMonth = getMonthes(yearProduct.period) * monthProduct.price;
            const { discount } = getDiscount({
                discountPrice: yearProduct?.discountPrice || yearProduct.price,
                price: yearPriceByMonth,
            });

            return discount;
        });
        // tempexp_18086-start
        if (ref_id && referralTariffs) {
            discounts = tariffs.map((tariff) => {
                const { monthProduct } = referralTariffs[tariff.space.original] || {};
                const { discount } = getDiscount({
                    discountPrice: monthProduct?.discountPrice || 0,
                    price: monthProduct?.price || 0,
                });
                return discount;
            });
        }
        // tempexp_18086-end
        return discounts.length && Math.max(...discounts);
    }
);

// tempexp_19495-start
export const getOverquotaTariffsList = createSelector(
    [
        (state: RootState, isMobile) => isMobile,
        (state: RootState) => {
            const list: YearAndMonthProductIds = {
                year: tariffOverquotaList?.year || multiTariffAdvancedTariffList?.year, // не воровство, а фоллбек!
                month: tariffOverquotaList?.month || multiTariffAdvancedTariffList?.month,
            };

            return ProductsSelectors.getNormalizedTariffsListByProductIds(state, list, 'overquota_sale');
        },
    ],
    (isMobile, overquotaTariffs): ITariffsByTab[] | ITariffsMobile[] => {
        if (!isMobile) {
            return overquotaTariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
                acc.push({
                    space: tariffItem.space,
                    tariffId: tariffItem.tariffId,
                    isRecommended: tariffItem.tariffId.includes(RECOMMENDED_ID_FAVORABLE),
                    products: tariffItem.products,
                });
                return acc;
            }, []);
        }
        const mobileTariffList = overquotaTariffs.reduce((acc, item) => {
            const monthTariffs: [Product | undefined, Product | undefined] = [item.products.monthProduct, undefined];
            acc[item.products.yearProduct.id] = [item.products.yearProduct, ...monthTariffs];
            return acc;
        }, <Record<string, [Product, Product | undefined, Product | undefined]>>{});

        return normalizeMobileTariffList(mobileTariffList);
    }
);
// tempexp_19495-end

export const getTariffsList = createSelector(
    [
        (state: RootState) => ProductsSelectors.getTariffsForQuotaLanding(state),
        (state: RootState) => ProductsSelectors.getPromoTariffs(state, true),
        // tempexp_16481-next-line
        (state: RootState, isMobile) => isMobile,
        (state: RootState, _, minTariff?: number) => minTariff,
        (state: RootState, _, __, forceFamilyTariffFromQuery?: boolean) => forceFamilyTariffFromQuery,
        (state: RootState, _, __, ___, mayPromo2025Enabled?: boolean) => mayPromo2025Enabled,
        (state: RootState) => getBaseUserQuota(state),
        // tempexp_16373-next-line
        (state: RootState) => ProductsSelectors.getFavorableTariffList(state),
        (state: RootState) => ProductsSelectors.getNormalizedProductsBySpace(state, additionalTariffsList?.tariffs || []),
        (state: RootState) => ProductsSelectors.getPreferredTariffs(state),
        (state: RootState) => ProductsSelectors.getThreeMonthPeriodTariffs(state),
        (state: RootState) => ProductsSelectors.getAvailableProductsByListIds(state, abProfitableTariffsFirstTouch.tariffs),
        (state: RootState) => ProductsSelectors.getCheapestProducts(state),
        (state: RootState) => UserSelectors.isPaidUser(state),
        // tempexp-17509-start
        (state: RootState, isMobile) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, isMobile ? litresTariffIdsList : litresTariffIds3mDiscountList),
        // tempexp-17509-end
        // tempexp_18795-next-line
        (state: RootState) => UserSelectors.isAlmostFullFreeUser(state),
        // tempexp_18086-start
        (state: RootState) => ReferralProgramSelectors.getRefId(state),
        (state: RootState) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...(referralProgramTariffs?.month || []),
                ...(referralProgramTariffs?.year || []),
            ]),
        // tempexp_18086-end
        (state: RootState) => ProductsSelectors.getTariffById(state, familyTariffIds.name),
        (state: RootState) => ProductsSelectors.getProductById(state, familyTariffIds.month),
        (state: RootState) => ProductsSelectors.getTariffById(state, familyTariffIdFromQuery),
        (state: RootState) => UserSelectors.isOverQuotaUser(state),
        (state: RootState) => ProductsSelectors.getMayPromo2025Tariff(state),
        (state: RootState) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(
                state,
                { year: [expensivePlanTariffIds.year], month: [expensivePlanTariffIds.month] },
                'expensive_landing'
            ),
    ],
    (
        nonSpecialTariffs,
        promoTariffs,
        isMobile,
        minTariff,
        forceFamilyTariffFromQuery,
        mayPromo2025Enabled,
        baseQuota,
        // tempexp_16373-next-line
        favorableTariffs,
        additionalTariffs,
        preferredTariffs,
        threeMonthPeriodTariffs,
        touchProfitableTariffs,
        cheapestProducts,
        isPaidUser,
        // tempexp-17509-next-line
        litressTariffs,
        // tempexp_18795-next-line
        isAlmostFullFreeUser,
        // tempexp_18086-start
        ref_id,
        refferalTariffs,
        // tempexp_18086-end
        familyTariffsLoaded,
        familyMonthProduct,
        familyTariffsFromQuery,
        isOverQuota,
        mayPromo2025Tariff,
        expensivePlanTariffs
    ): ITariffsByTab[] | ITariffsMobile[] => {
        let tariffs: Tariff[] = [];

        // В задаче CLOUDWEB-17222 в варианте 'c' базовые годовые тарифы без скидки, поэтому промо тарифы не показываем
        tariffs = promoTariffs?.length && additionalTariffsList?.variant !== 'c' ? promoTariffs : nonSpecialTariffs;

        const showFamilyTariff = !ref_id && (enabledFamilyTariff || forceFamilyTariffFromQuery);
        const familyTariffs = forceFamilyTariffFromQuery ? familyTariffsFromQuery : familyTariffsLoaded;
        const familyTariffId = forceFamilyTariffFromQuery ? familyTariffIdFromQuery : familyTariffIds.name;

        const expensivePlanTariff = expensivePlanTariffs[0];

        // tempexp_16373-next-line
        if (favorableTariffs.length) {
            tariffs = favorableTariffs;
        }

        if (preferredTariffs.length) {
            tariffs = preferredTariffs;
        } else if (threeMonthPeriodTariffs.length) {
            tariffs = tariffs.map((tariff) => {
                const threeMonthPeriodProduct = getProductFromTariffListByQuotaAndPeriod(
                    threeMonthPeriodTariffs,
                    tariff.space.original,
                    PERIODS.threeMonths
                );
                const products = [...tariff.products];
                if (threeMonthPeriodProduct) {
                    products.push(threeMonthPeriodProduct);
                }

                return {
                    ...tariff,
                    products,
                };
            });
        }

        if (mayPromo2025Enabled && mayPromo2025Tariff) {
            const index = tariffs.findIndex((tariff) => tariff.space.original === SPACE_LIST.tb_1);
            if (index !== -1) {
                tariffs.splice(index, 1);
            }
            tariffs.splice(1, 0, mayPromo2025Tariff);
        }

        if (showFamilyTariff && familyTariffs) {
            tariffs.push(familyTariffs);
        }

        let list = tariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
            const products = getTariffProductsByPeriods(tariffItem.products);

            let familyTariff;
            if (showFamilyTariff && tariffItem.id === familyTariffId) {
                familyTariff = getTariffProductsByPeriods(tariffItem.products);
            }

            // const additionalTariff = !cyberMonday2025 ? additionalTariffs[tariffItem.space.original] : undefined;
            const additionalTariff = additionalTariffs[tariffItem.space.original];

            // tempexp-17509-start
            const litresTariff = isPromoQuotaWithLitress && !isPaidUser ? litressTariffs[tariffItem.space.original] : undefined;
            // tempexp-17509-end

            // tempexp_18086-start
            const referralProducts = ref_id ? refferalTariffs[tariffItem.space.original] : undefined;
            // tempexp_18086-end

            if (products.yearProduct) {
                let recommendedId = RECOMMENDED_ID;

                if (favorableTariffsIDs.length) {
                    recommendedId = RECOMMENDED_ID_FAVORABLE;
                }

                const yearProduct =
                    // tempexp-18086-next-line
                    referralProducts?.yearProduct || // !!! Если продукты реферальной программы доступны, они первые в списке
                    litresTariff?.yearProduct ||
                    additionalTariff?.yearProduct ||
                    products.yearProduct;
                const monthProduct =
                    // tempexp-18086-next-line
                    referralProducts?.monthProduct ||
                    (familyTariff && (familyMonthProduct || familyTariff?.monthProduct)) ||
                    (mayPromo2025Enabled && mayPromo2025Tariff && tariffItem.id === mayPromo2025YearProduct && products.monthProduct) ||
                    litresTariff?.monthProduct ||
                    additionalTariff?.monthProduct ||
                    products.monthProduct;
                const threeMonthProduct = products.threeMonthProduct;

                let recommendedTariffId = favorableTariffsIDs.length ? RECOMMENDED_ID_FAVORABLE : recommendedId;

                if (showFamilyTariff && !enabledExpensivePlanTrial) {
                    recommendedTariffId = familyTariffId;
                }

                if (enabledExpensivePlanTrial && !ref_id) {
                    recommendedTariffId = expensivePlanTariff?.tariffId;
                }

                let isRecommended = cheapestTariff === 'a' || isPaidUser ? tariffItem.id.includes(recommendedTariffId) : false;

                if (mayPromo2025Enabled && mayPromo2025Tariff) {
                    isRecommended = tariffItem.id === mayPromo2025Tariff.id;
                }

                acc.push({
                    space: tariffItem.space,
                    tariffId: tariffItem.id,
                    // tempexp_16373-next-line
                    isRecommended,
                    products: preferredTariffs.length
                        ? products
                        : {
                              yearProduct,
                              monthProduct,
                              threeMonthProduct,
                          },
                });
            }

            return acc;
        }, []);

        if (enabledExpensivePlanTrial && expensivePlanTariff && !ref_id) {
            list[0] = { ...expensivePlanTariff, isRecommended: true };
        }

        // tempexp_16373-next-line
        if (!favorableTariffsIDs.length) {
            list.unshift({
                tariffId: 'base',
                space: { ...baseQuota },
                isRecommended: false,
                isBase: true,
                products: {
                    yearProduct: {
                        ...baseYearProduct,
                        space: baseQuota,
                    },
                    monthProduct: undefined,
                },
            });
        }

        const minTariffParam = minTariff && minTariff > 0 ? minTariff : isMobile ? touchTariffMinSize : desktopTariffMinSize;
        const minTariffSize = SPACE_SIZES[`_${minTariffParam}`];

        if (minTariffSize > 0) {
            list = list.filter((tariff) => tariff?.space?.original >= minTariffSize);
        }

        if (!isMobile && cheapestProducts.yearProduct && cheapestProducts.monthProduct && !isPaidUser) {
            const cheapestTariffTabOptions = {
                tariffId: cheapestTariffID,
                space: { ...baseQuota }, // Так как квота 1Кб, то используется базовый тариф
                isRecommended: true,
                isCheapest: true,
                products: cheapestProducts,
            };
            if (cheapestTariff === 'b') {
                list.unshift(cheapestTariffTabOptions);
            }
            if (cheapestTariff === 'c') {
                list.push(cheapestTariffTabOptions);
            }
        }

        if (showFamilyTariff) {
            const lastElemList = list.pop();
            if (lastElemList) {
                list.splice(1, 0, lastElemList);
            }
        }

        if (isMobile) {
            // tempexp-16876-start
            // список id годовых продуктов
            const profitableTariffs = abProfitableTariffsFirstTouch.tariffs;

            const sortedTouchProfitableTariffs = profitableTariffs.map((productYearId) =>
                touchProfitableTariffs.find((tariff) => tariff.products.some((product) => product.id === productYearId))
            );

            // tempexp_17242-next-line
            if (profitableTariffs?.length && !preferredTariffs?.length) {
                const sortedList: ITariffsByTab[] = [];
                sortedTouchProfitableTariffs.forEach((tariff) => {
                    const foundItem = list.find((t) => t.tariffId === tariff?.id);
                    if (foundItem) {
                        sortedList.push(foundItem);
                    }
                });

                list = sortedList;

                // tempexp_17221-next-line: Исключаем тарифы которых нет в массиве из фичи.
                list = list.filter((item) => profitableTariffs.includes(item.products.yearProduct.id));
            }
            // tempexp-16876-end

            const mobileTariffList = list.reduce((acc, item) => {
                // не показываем базовый тариф на таче
                if (item.isBase) {
                    return acc;
                }

                // tempexp_18795-start
                // если тариф в списке на исключение, не добавляем его
                if (isAlmostFullFreeUser && excludeTariffsForFreeUsers?.quotas?.includes(item.space.original)) {
                    return acc;
                }
                // tempexp_18795-end

                if (isOverQuota && excludeTariffsMobileOverquoters?.quotas?.includes(item.space.original)) {
                    return acc;
                }

                // tempexp_18796-start
                const excludeMonth = excludeTariffs && excludeTariffs.month?.some((quota) => quota === item.space.original);

                const monthTariffs: [Product | undefined, Product | undefined] = excludeMonth
                    ? [undefined, undefined]
                    : [item.products.monthProduct, item.products.threeMonthProduct];
                // tempexp_18796-end

                acc[item.products.yearProduct.id] = [item.products.yearProduct, ...monthTariffs];

                return acc;
            }, <Record<string, [Product, Product | undefined, Product | undefined]>>{});

            return normalizeMobileTariffList(mobileTariffList, Boolean(promoTariffs?.length));
        }

        return list;
    }
);

// tempexp_17380-start
export const getTariffListPaidUsers = createSelector(
    [
        (state: RootState) => ProductsSelectors.getTariffsForQuotaLanding(state),
        // tempexp_16373-next-line
        (state: RootState) => ProductsSelectors.getFavorableTariffList(state),
    ],
    (nonSpecialTariffs, favorableTariffs): ITariffsByTab[] => {
        const items: ITariffsByTab[] = [];

        for (const tariff of favorableTariffs) {
            const item = getTariffsByQuotaHelper(nonSpecialTariffs, tariff.space.original)[0];
            const products = getTariffProductsByPeriods(item.products);
            const isRecommended = item.id.includes(RECOMMENDED_ID_FAVORABLE);

            items.push({
                space: item.space,
                tariffId: item.id,
                isRecommended,
                products,
            });
        }

        return items;
    }
);
// tempexp_17380-end

// tempexp_17815-start
export const getTariffListPaidUsersMobile = createSelector(
    [(state: RootState) => ProductsSelectors.getTariffsForQuotaLanding(state)],
    (nonSpecialTariffs): ITariffsMobile[] => {
        const mobileTariffList = nonSpecialTariffs.reduce((acc, item) => {
            const products = getTariffProductsByPeriods(item.products);

            if (item.space.original < SPACE_LIST.gb_64) {
                return acc;
            }

            acc[item.space.original] = [products.yearProduct, products.monthProduct];
            return acc;
        }, <Record<number, [Product, Product | undefined]>>{});

        return normalizeMobileTariffList(mobileTariffList, false);
    }
);
// tempexp_17815-end

export const getMultiTariffList = createSelector(
    [
        (_, isOverQuota: boolean) => isOverQuota,
        (state: RootState, isOverQuota) => {
            let list = multiTariffAdvancedTariffList;
            // tempexp_19411-start - Повышаем для оверквотников цены
            if (isOverQuota && isMultiTariffAdvancedOverquota) {
                list = {
                    year: multiTariffAdvancedOverquotaList?.year || list.year,
                    month: multiTariffAdvancedOverquotaList?.month || list.month,
                };
            }
            // tempexp_19411-end
            return ProductsSelectors.getNormalizedTariffsListByProductIds(state, list, 'landing-segmentation-advanced');
        },
        (state: RootState, _) =>
            ProductsSelectors.getNormalizedTariffsListByProductIds(state, multiTariffBasicTariffList, 'landing-segmentation-basic'),
        (state: RootState, _) => ProductsSelectors.getTariffById(state, multiTariffFamilyTariff),
    ],
    (_, advancedTariffs, basicTariffs, familyTariff) => {
        const basicList = basicTariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
            if (tariffItem.products.yearProduct && tariffItem.products.monthProduct) {
                acc.push({
                    space: tariffItem.space,
                    tariffId: tariffItem.tariffId,
                    isRecommended: ['W1TB-landing-segmentation-basic'].includes(tariffItem.tariffId),
                    isMultiBasic: true,
                    products: tariffItem.products,
                });
            }

            return acc;
        }, []);

        const advancedList = advancedTariffs.reduce((acc: ITariffsByTab[], tariffItem) => {
            if (tariffItem.products.yearProduct) {
                acc.push({
                    space: tariffItem.space,
                    tariffId: tariffItem.tariffId,
                    isRecommended: ['W1TB-landing-segmentation-advanced'].includes(tariffItem.tariffId),
                    isMultiAdvanced: true,
                    products: tariffItem.products,
                });
            }

            return acc;
        }, []);

        let family;

        if (familyTariff) {
            const familyProducts = getTariffProductsByPeriods(familyTariff.products);

            family = {
                space: familyTariff.space,
                tariffId: familyTariff.id,
                products: familyProducts,
            };
        }

        return {
            basic: basicList.reverse(),
            advanced: advancedList.reverse(),
            family,
            load: true,
        };
    }
);

export const getMultiTariffMobileProductsSpacesListByTab = createSelector(
    [
        (_, isAction: boolean) => isAction,
        (state: RootState, _) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...multiTariffBasicTariffListMobile.year,
                ...multiTariffBasicTariffListMobile.month,
            ]),
        (state: RootState, _) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...multiTariffAdvancedTariffListMobile.year,
                ...multiTariffAdvancedTariffListMobile.month,
            ]),
        (state: RootState, _) =>
            ProductsSelectors.getNormalizedProductsBySpace(state, [
                ...multiTariffFamilyTariffListMobile.year,
                ...multiTariffFamilyTariffListMobile.month,
            ]),
        (state: RootState, _) => getMultiTariffMobileActiveTab(state),
        (state: RootState, _) => ProductsSelectors.getProductById(state, mayPromo2025YearProductId),
    ],
    (isAction, basicTariffs, advancedTariffs, familyTariffs, activeTab, mayTrial) => {
        let products: Record<number, { yearProduct: undefined | Product; monthProduct: undefined | Product }> = {};
        let yearProductIDs: string[] = [];

        if (activeTab === ETabsName.basic) {
            products = basicTariffs;
            yearProductIDs = multiTariffBasicTariffListMobile.year;
        } else if (activeTab === ETabsName.advanced) {
            products = advancedTariffs;

            if (mayTrial && isAction) {
                const spaceTrialProduct = advancedTariffs[mayTrial.space.original];

                products = {
                    ...advancedTariffs,
                    [mayTrial.space.original]: {
                        ...spaceTrialProduct,
                        yearProduct: mayTrial,
                    },
                };
            }

            yearProductIDs = multiTariffAdvancedTariffListMobile.year;
        } else if (activeTab === ETabsName.family) {
            products = familyTariffs;
            yearProductIDs = multiTariffFamilyTariffListMobile.year;
        }

        const spaces = Object.values(products)
            .sort((a, b) => {
                if (a.yearProduct && b.yearProduct) {
                    return yearProductIDs.indexOf(a.yearProduct.id) - yearProductIDs.indexOf(b.yearProduct.id);
                }
                return 0;
            })
            .map((item) => item.yearProduct?.space.original || 0)
            .filter(Boolean);

        return { products, spaces, selectorDataLoaded: true };
    }
);
